import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ---- Palette ----
const COLORS = {
  cardBg: "rgba(0,0,0,0.35)",
  border: "rgba(255,255,255,0.07)",
  borderStrong: "rgba(255,255,255,0.15)",
  text: "#FFFFFF",
  textSecondary: "#A39992",
  textMuted: "rgba(255,255,255,0.4)",
  red: "#EB1700",
  green: "#4ADE80",
  blue: "rgba(96,165,250,0.9)",
  amber: "rgba(251,191,36,0.9)",
};

// ---- Shared style fragments ----
const sectionTitleStyle = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: COLORS.textSecondary,
  margin: 0,
};

const cardStyle = {
  background: "rgba(255,255,255,0.04)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  borderRadius: 8,
  border: `1px solid rgba(255,255,255,0.1)`,
};

const sectionAnim = (index) => ({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay: index * 0.06 },
});

function Section({ index, children, style }) {
  return (
    <motion.div
      {...sectionAnim(index)}
      style={{ display: "flex", flexDirection: "column", gap: 12, ...style }}
    >
      {children}
    </motion.div>
  );
}

function SectionHeader({ title, badge }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: "rgba(255,255,255,0.04)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 6,
      padding: "8px 12px",
    }}>
      <h2 style={sectionTitleStyle}>{title}</h2>
      {badge}
    </div>
  );
}

function Badge({ children, color = COLORS.textMuted, bg }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color,
        background: bg ?? "rgba(255,255,255,0.06)",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 999,
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// ============================================================
// Section 1 — KPI Cards
// ============================================================
const KPIS = [
  { title: "Total Open POs", value: "247", delta: "+12 this week", deltaColor: COLORS.green, subtitle: "Across 8 Novant facilities" },
  { title: "Pending Approvals", value: "18", delta: "3 overdue", deltaColor: COLORS.amber, subtitle: "Avg wait: 1.4 days" },
  { title: "Delayed Orders", value: "12", delta: "-4 vs last week", deltaColor: COLORS.red, subtitle: "On-time: 95.1%" },
  { title: "Blocked Orders", value: "3", delta: "1 critical", deltaColor: COLORS.red, subtitle: "Requires immediate action" },
  { title: "Total PO Spend", value: "$4.2M", delta: "+8.3% MTD", deltaColor: COLORS.green, subtitle: "Budget utilization: 71%" },
  { title: "Contract Compliance", value: "94.3%", delta: "+1.2pp", deltaColor: COLORS.green, subtitle: "Target: ≥95%" },
  { title: "Fill Rate", value: "97.8%", delta: "-0.4pp", deltaColor: COLORS.red, subtitle: "J&J MedTech SLA: 98%" },
  { title: "Avg PO Cycle Time", value: "3.2 days", delta: "-0.5 vs target", deltaColor: COLORS.green, subtitle: "Benchmark: 3.7 days" },
];

function KPICards({ index }) {
  return (
    <Section index={index}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {KPIS.map((k) => (
          <div key={k.title} style={{ ...cardStyle, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: COLORS.textSecondary, fontWeight: 600 }}>
              {k.title}
            </span>
            <span style={{ fontSize: 28, fontWeight: 700, color: COLORS.text, lineHeight: 1.1 }}>{k.value}</span>
            <span style={{ fontSize: 12, color: k.deltaColor, fontWeight: 600 }}>{k.delta}</span>
            <span style={{ fontSize: 11, color: COLORS.textMuted }}>{k.subtitle}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ============================================================
// Section 2 — Risk & Exception Management
// ============================================================
const RISKS = [
  { icon: "⚠", severity: "HIGH", color: COLORS.red, title: "Delayed Shipment", desc: "PO#4821 delayed 2 days — Trocars for Jun 21 OR", cause: "Root cause: Carrier rerouting due to weather. Recommend: Request expedited shipping, notify OR scheduler.", pos: "1 PO" },
  { icon: "≠", severity: "HIGH", color: COLORS.red, title: "Invoice Mismatch", desc: "INV-2887 has $1,240 discrepancy vs PO#4815", cause: "Root cause: Unit price updated post-PO without contract amendment. Escalate to AP.", pos: "1 PO" },
  { icon: "⊘", severity: "HIGH", color: COLORS.red, title: "Missing Acknowledgement", desc: "PO#4825 unacknowledged 48hrs", cause: "Root cause: Supplier portal timeout. Agent retrying via EDI fallback.", pos: "1 PO" },
  { icon: "$", severity: "MEDIUM", color: COLORS.amber, title: "Pricing Discrepancy", desc: "3 line items 4-7% over contract price", cause: "Contract pricing table not refreshed after Jan renewal.", pos: "3 POs" },
  { icon: "⧉", severity: "MEDIUM", color: COLORS.amber, title: "Duplicate PO Risk", desc: "PO#4798 ~PO#4761 same items/supplier/week", cause: "Auto-consolidation blocked pending user approval.", pos: "2 POs" },
  { icon: "⌛", severity: "MEDIUM", color: COLORS.amber, title: "Backorder >7 Days", desc: "4 SKUs with no ETA from supplier", cause: "Recommend activating alternate supplier PO.", pos: "4 POs" },
  { icon: "↓", severity: "MEDIUM", color: COLORS.amber, title: "Supplier Performance", desc: "J&J fill rate dropped to 97.2% (SLA: 98%)", cause: "3 consecutive weeks below SLA — flag for QBR.", pos: "—" },
  { icon: "⏷", severity: "LOW", color: COLORS.green, title: "Contract Expiry Warning", desc: "2 contracts expire in 45 days", cause: "Auto-renewal workflow triggered, pending procurement approval.", pos: "—" },
];

function RiskManagement({ index }) {
  return (
    <Section index={index}>
      <SectionHeader title="Risk & Exception Management" badge={<><Badge color={COLORS.red} bg="rgba(235,23,0,0.12)">3 High</Badge><Badge color={COLORS.amber} bg="rgba(251,191,36,0.12)">6 Medium</Badge><Badge>3 Low</Badge></>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {RISKS.map((r, i) => (
          <div key={i} style={{ ...cardStyle, padding: 14, display: "flex", flexDirection: "column", gap: 6, borderTop: `2px solid ${r.color}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14, color: r.color }}>{r.icon}</span>
              <Badge color={r.color} bg={`${r.color === COLORS.red ? "rgba(235,23,0,0.12)" : r.color === COLORS.amber ? "rgba(251,191,36,0.12)" : "rgba(74,222,128,0.12)"}`}>{r.severity}</Badge>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{r.title}</span>
            <span style={{ fontSize: 11, color: COLORS.textSecondary }}>{r.desc}</span>
            <span style={{ fontSize: 11, fontStyle: "italic", color: COLORS.textMuted }}>{r.cause}</span>
            <span style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>Affected: {r.pos}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ============================================================
// Section 3 — Order Tracking
// ============================================================
const TRACKING_STAGES = ["Created", "Approved", "Sent", "Acknowledged", "Shipped", "Received", "Invoiced", "Paid"];

const TRACKING_POS = [
  { po: "PO#4821", supplier: "J&J MedTech", value: "$34,200", stage: "Shipped", eta: "Jun 19", statusColor: COLORS.red, statusLabel: "Delayed 2 days" },
  { po: "PO#4815", supplier: "J&J MedTech", value: "$12,800", stage: "Invoiced", eta: "—", statusColor: COLORS.amber, statusLabel: "Invoice mismatch" },
  { po: "PO#4809", supplier: "J&J MedTech", value: "$28,500", stage: "Acknowledged", eta: "Jun 21", statusColor: COLORS.green, statusLabel: "On track" },
  { po: "PO#4799", supplier: "J&J MedTech", value: "$8,900", stage: "Received", eta: "—", statusColor: COLORS.green, statusLabel: "Awaiting payment" },
  { po: "PO#4831", supplier: "J&J MedTech", value: "$12,400", stage: "Sent", eta: "Jun 23", statusColor: COLORS.green, statusLabel: "On track" },
];

function OrderTracking({ index }) {
  return (
    <Section index={index}>
      <SectionHeader title="Order Tracking" />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {TRACKING_POS.map((po) => {
          const currentIdx = TRACKING_STAGES.indexOf(po.stage);
          return (
            <div key={po.po} style={{ ...cardStyle, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{po.po}</span>
                  <span style={{ fontSize: 11, color: COLORS.textSecondary }}>{po.supplier}</span>
                  <span style={{ fontSize: 11, color: COLORS.text, fontWeight: 600 }}>{po.value}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 11, color: COLORS.textMuted }}>ETA: {po.eta}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: po.statusColor }}>● {po.statusLabel}</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                {TRACKING_STAGES.map((stage, i) => {
                  const isCurrent = i === currentIdx;
                  const isComplete = i < currentIdx;
                  const dotColor = isCurrent ? COLORS.blue : isComplete ? COLORS.green : "rgba(255,255,255,0.2)";
                  const dotSize = isCurrent ? 12 : isComplete ? 8 : 7;
                  return (
                    <div key={stage} style={{ display: "flex", alignItems: "center", flex: i === TRACKING_STAGES.length - 1 ? "0 0 auto" : 1 }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        <span style={{ width: dotSize, height: dotSize, borderRadius: "50%", background: dotColor, boxShadow: isCurrent ? `0 0 8px ${COLORS.blue}` : "none", flexShrink: 0 }} />
                        <span style={{ fontSize: 9, color: isCurrent ? COLORS.text : COLORS.textMuted, fontWeight: isCurrent ? 700 : 400, whiteSpace: "nowrap" }}>{stage}</span>
                      </div>
                      {i < TRACKING_STAGES.length - 1 && (
                        <span style={{ flex: 1, height: 2, background: i < currentIdx ? COLORS.green : "rgba(255,255,255,0.12)", margin: "0 4px", marginBottom: 16 }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ============================================================
// Shared building blocks for agent dashboards
// ============================================================
function StatCards({ index, cards }) {
  return (
    <Section index={index}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {cards.map((c) => (
          <div key={c.title} style={{ ...cardStyle, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: COLORS.textSecondary, fontWeight: 600 }}>
              {c.title}
            </span>
            <span style={{ fontSize: 28, fontWeight: 700, color: COLORS.text, lineHeight: 1.1 }}>{c.value}</span>
            <span style={{ fontSize: 11, color: c.captionColor ?? COLORS.textMuted, fontWeight: c.captionColor ? 600 : 400 }}>{c.caption}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

const tableCellStyle = { fontSize: 11, color: COLORS.textSecondary, padding: "10px 12px", textAlign: "left" };
const tableHeadStyle = { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: COLORS.textMuted, fontWeight: 700, padding: "8px 12px", textAlign: "left" };

function DataTable({ columns, rows, renderCell, onRowClick, selectedRow }) {
  return (
    <div style={{ ...cardStyle, padding: 4, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col} style={tableHeadStyle}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              onClick={() => onRowClick?.(row)}
              style={{
                borderTop: `1px solid ${COLORS.border}`,
                cursor: onRowClick ? "pointer" : "default",
                background: selectedRow?.po === row.po ? "rgba(255,255,255,0.04)" : "transparent",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = "rgba(255,255,255,0.04)" }}
              onMouseLeave={e => { if (onRowClick && selectedRow?.po !== row.po) e.currentTarget.style.background = "transparent" }}
            >
              {columns.map((col, ci) => (
                <td key={col} style={tableCellStyle}>{renderCell(row, col, ci)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusDot({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
}

const SHIPMENT_TRACKING_STEPS = [
  { label: "Order Placed", done: true },
  { label: "Acknowledged", done: true },
  { label: "Picked Up", done: true },
  { label: "In Transit", done: true },
  { label: "Out for Delivery", done: false },
  { label: "Delivered", done: false },
];

const SHIPMENT_DETAILS = {
  "PO#4862": {
    imgBg: "#1a1a3a",
    imgLabel: "AK",
    imgSrc: "https://www.jnjmedtech.com/sites/default/files/styles/product_hub_category_max_2_rows/public/2024-10/ATTUNE%20KNEE%20SYSTEM%20866x866.jpg?h=84ebcd40&itok=MnnHOKhI",
    sku: "DPS-ATN-7",
    department: "Orthopedics — Novant Presbyterian",
    origin: "DePuy Synthes — Warsaw, IN",
    destination: "Novant Presbyterian — Charlotte, NC",
    carrier: "FedEx Priority",
    trackingNo: "7489 2345 6780",
    weight: "18.4 lbs",
    dimensions: "24\" × 16\" × 12\"",
    currentLocation: "Greensboro, NC",
    lastUpdate: "Jun 23, 5:14 AM",
    steps: [
      { label: "Order Placed", done: true, time: "Jun 20, 8:00 AM" },
      { label: "Acknowledged", done: true, time: "Jun 20, 9:32 AM" },
      { label: "Picked Up", done: true, time: "Jun 21, 2:15 PM" },
      { label: "In Transit", done: true, time: "Jun 22, 11:40 PM" },
      { label: "Out for Delivery", done: false, time: "Expected Jun 23, 7:30 AM" },
      { label: "Delivered", done: false, time: "—" },
    ],
    risk: "Carrier scans project delivery on Wed Jun 25 — one day after the confirmed ETA and after Dr. Alvarez's total knee scheduled for Tue Jun 23 at 7:30 AM in OR 4. J&J's system still shows Tuesday. The set needs to be on-site before the case; the actions below resolve this without rescheduling.",
  },
};

const DETAIL_TABS = ["Tracking", "Shipment Info", "Case Info"];

function ShipmentTabs({ steps, detail }) {
  const [active, setActive] = useState("Tracking");

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
      style={{ background: "rgba(0,0,0,0.3)", borderRadius: 4, overflow: "hidden" }}
    >
      {/* Tab bar */}
      <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        {DETAIL_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActive(tab)}
            style={{
              flex: 1,
              padding: "9px 0",
              background: "none",
              border: "none",
              borderBottom: `2px solid ${active === tab ? "#FFFFFF" : "transparent"}`,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: active === tab ? "#FFFFFF" : "#A39992",
              transition: "color 0.15s, border-color 0.15s",
              marginBottom: -1,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}
        >
          {active === "Tracking" && steps.map((step, i) => (
            <div key={step.label} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 12, flexShrink: 0 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: step.done ? COLORS.green : "rgba(255,255,255,0.12)", border: step.done ? "none" : "1px solid rgba(255,255,255,0.2)", marginTop: 3, flexShrink: 0 }} />
                {i < steps.length - 1 && <div style={{ width: 1, flex: 1, minHeight: 18, background: step.done ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.06)", margin: "3px 0" }} />}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, paddingBottom: i < steps.length - 1 ? 6 : 0 }}>
                <span style={{ fontSize: 12, fontWeight: step.done ? 700 : 400, color: step.done ? "#FFFFFF" : "#D5CFC9", lineHeight: 1.4 }}>{step.label}</span>
                {step.time && <span style={{ fontSize: 11, color: "#A39992" }}>{step.time}</span>}
              </div>
            </div>
          ))}

          {active === "Shipment Info" && detail && [
            ["Tracking #", detail.trackingNo],
            ["From", detail.origin],
            ["To", detail.destination],
            ["Current Location", detail.currentLocation],
            ["Last Update", detail.lastUpdate],
            ["Carrier", detail.carrier],
            ["Weight", detail.weight],
            ["Dimensions", detail.dimensions],
          ].map(([label, val]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 12, color: "#D5CFC9" }}>{label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#FFFFFF", textAlign: "right" }}>{val}</span>
            </div>
          ))}

          {active === "Case Info" && (
            <>
              {/* Patient Info */}
              <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>Patient</span>
              {["Name", "MRN", "DOB"].map((label) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#D5CFC9" }}>{label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.3)", textAlign: "right" }}>Confidential</span>
                </div>
              ))}

              {/* Divider */}
              <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "4px 0" }} />

              {/* Case Info */}
              <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>Case</span>
              {[
                ["Procedure", "Total Knee Replacement"],
                ["Surgeon", "Dr. Alvarez"],
                ["OR", "OR 4"],
                ["Facility", "Novant Presbyterian"],
                ["Date & Time", "Tue Jun 23, 7:30 AM"],
              ].map(([label, val]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#D5CFC9" }}>{label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#FFFFFF", textAlign: "right" }}>{val}</span>
                </div>
              ))}
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}

const detailLabelStyle = {
  fontSize: 10,
  fontWeight: 700,
  color: "#A39992",
  textTransform: "uppercase",
  letterSpacing: "0.09em",
};

export function ShipmentDetailPanel({ row, onClose, autonomyLevel = 0 }) {
  const detail = SHIPMENT_DETAILS[row.po] ?? null;
  const steps = detail?.steps ?? SHIPMENT_TRACKING_STEPS;

  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: 520, opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      style={{ flexShrink: 0, overflowX: "hidden", overflowY: "auto", height: "100%", background: "rgba(0,0,0,0.5)", borderRadius: 4 }}
    >
      <div style={{ width: 520, padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>

        {/* Header row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: 28, flexShrink: 0 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={detailLabelStyle}>Shipment Detail</span>
          </div>
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
            style={{ width: 28, height: 28, borderRadius: 8, background: "transparent", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#A39992" }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </motion.button>
        </motion.div>

        {/* PO summary card — delivery card style */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
          style={{ ...cardStyle, overflow: "hidden", flexShrink: 0, display: "flex", flexDirection: "row" }}
        >
          {/* Image area */}
          <div style={{ width: 120, flexShrink: 0, background: detail?.imgBg ?? "#1a1a2a", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", borderRight: "1px solid rgba(255,255,255,0.07)" }}>
            {detail?.imgSrc
              ? <img src={detail.imgSrc} alt={row.product} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
              : <span style={{ fontSize: 32, fontWeight: 800, color: "rgba(255,255,255,0.15)", letterSpacing: "0.06em" }}>{detail?.imgLabel ?? "–"}</span>
            }
          </div>
          {/* Info */}
          <div style={{ flex: 1, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{row.po} · {detail?.sku ?? row.supplier}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.3 }}>{row.product}</span>
            <span style={{ fontSize: 11, color: "#A39992" }}>{detail?.department ?? row.supplier}</span>
            <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 5, background: "rgba(0,0,0,0.3)", borderRadius: 20, padding: "2px 8px", border: `1px solid ${row.color}`, alignSelf: "flex-start" }}>
              <span style={{ fontSize: 9, fontWeight: 600, color: row.color }}>{row.eta}</span>
              <span style={{ fontSize: 8, color: "rgba(255,255,255,0.25)" }}>·</span>
              <span style={{ fontSize: 9, fontWeight: 700, color: row.color }}>{row.status}</span>
            </div>
            {detail?.risk && (
              <div style={{ marginTop: 6, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={detailLabelStyle}>Risk Alert</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.4 }}>The ATTUNE set for Dr. Alvarez's Tue 7:30 case will arrive a day late.</span>
                <span style={{ fontSize: 11, fontWeight: 400, color: "#D5CFC9", lineHeight: 1.4 }}>{detail.risk}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recommended actions */}
        {/* Actions section — varies by autonomy level */}
        <AnimatePresence mode="wait">
          {detail?.risk && autonomyLevel === 0 && (
            <motion.div
              key="copilot-actions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ background: "rgba(0,0,0,0.3)", borderRadius: 4, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}
            >
              <span style={detailLabelStyle}>Recommended Actions</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Action 1</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.4 }}>Borrow from South Park</span>
                <span style={{ fontSize: 12, fontWeight: 400, color: "#D5CFC9", lineHeight: 1.4 }}>
                  Novant's South Park campus has a matching ATTUNE set available — 40 minutes out, no cost impact, and it covers the case without delay.
                </span>
                <motion.button whileHover={{ scale: 1.02, filter: "brightness(1.1)" }} whileTap={{ scale: 0.97 }}
                  style={{ background: "#EB1700", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#FFFFFF", cursor: "pointer", fontFamily: "inherit", alignSelf: "flex-start", marginTop: 2 }}>
                  Transfer Set from South Park
                </motion.button>
              </div>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Action 2</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.4 }}>Expedite a replacement to South Park</span>
                <span style={{ fontSize: 12, fontWeight: 400, color: "#D5CFC9", lineHeight: 1.4 }}>
                  Once the borrowed set ships, South Park will be short one. Agent can file an expedite request with J&J now to restore their inventory before their next case.
                </span>
                <motion.button whileHover={{ scale: 1.02, filter: "brightness(1.1)" }} whileTap={{ scale: 0.97 }}
                  style={{ background: "#EB1700", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#FFFFFF", cursor: "pointer", fontFamily: "inherit", alignSelf: "flex-start", marginTop: 2 }}>
                  File Expedite Request with J&J
                </motion.button>
              </div>
            </motion.div>
          )}

          {autonomyLevel === 1 && (
            <motion.div
              key="semi-actions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ background: "rgba(0,0,0,0.3)", borderRadius: 4, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}
            >
              <span style={detailLabelStyle}>Actions Taken</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Action 1</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.4 }}>Transferred set from South Park</span>
                <span style={{ fontSize: 12, fontWeight: 400, color: "#D5CFC9", lineHeight: 1.4 }}>
                  Agent sourced a matching ATTUNE set from Novant's South Park campus and initiated the transfer. No cost impact — case is covered.
                </span>
                <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#FFFFFF", cursor: "pointer", fontFamily: "inherit" }}>
                    Undo
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#FFFFFF", cursor: "pointer", fontFamily: "inherit" }}>
                    Adjust
                  </motion.button>
                </div>
              </div>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Action 2</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.4 }}>Expedite request filed with J&J</span>
                <span style={{ fontSize: 12, fontWeight: 400, color: "#D5CFC9", lineHeight: 1.4 }}>
                  An expedite order was submitted to J&J MedTech to replenish South Park's inventory before their next case.
                </span>
                <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#FFFFFF", cursor: "pointer", fontFamily: "inherit" }}>
                    Undo
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#FFFFFF", cursor: "pointer", fontFamily: "inherit" }}>
                    Adjust
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {autonomyLevel === 2 && (
            <motion.div
              key="autonomous-log"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ background: "rgba(0,0,0,0.3)", borderRadius: 4, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10 }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={detailLabelStyle}>Activity Log</span>
                <motion.button whileHover={{ opacity: 0.8 }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, fontWeight: 600, color: COLORS.green, fontFamily: "inherit", padding: 0 }}>
                  View full log
                </motion.button>
              </div>
              {[
                { time: "Jun 20, 2:14 PM", text: "Delay risk detected — carrier scan cadence projected 1-day late arrival" },
                { time: "Jun 20, 2:15 PM", text: "Identified matching ATTUNE set at South Park campus — confirmed available" },
                { time: "Jun 20, 2:16 PM", text: "Transfer request initiated from South Park — no cost impact" },
                { time: "Jun 20, 2:18 PM", text: "Transfer confirmed — set en route, ETA Jun 23, 6:45 AM" },
                { time: "Jun 20, 2:19 PM", text: "Expedite request filed with J&J to replenish South Park inventory" },
              ].map((entry, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.green, flexShrink: 0, marginTop: 4 }} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <span style={{ fontSize: 9, fontWeight: 600, color: "rgba(255,255,255,0.35)", letterSpacing: "0.04em" }}>{entry.time}</span>
                    <span style={{ fontSize: 11, color: "#D5CFC9", lineHeight: 1.4 }}>{entry.text}</span>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div style={{ marginTop: 8 }}>
          <ShipmentTabs steps={steps} detail={detail} />
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================
// Status & Tracking Agent
// ============================================================
const SHIPMENT_KPIS = [
  { title: "In Transit", value: "28", caption: "Across 6 carriers" },
  { title: "Delayed", value: "3", caption: "2 weather, 1 customs", captionColor: COLORS.red },
  { title: "On Time Today", value: "12", caption: "100% of scheduled", captionColor: COLORS.green },
  { title: "Expected Today", value: "5", caption: "Next: 2:40 PM EST" },
];

const ACTIVE_SHIPMENTS = [
  { po: "PO#4821", product: "ECHELON FLEX Stapler 60mm", supplier: "J&J MedTech", stage: "In Transit", carrier: "FedEx Priority", eta: "Jun 19", color: COLORS.red, status: "Delayed 2 days" },
  { po: "PO#4809", product: "VICRYL Suture 3-0 (Box/36)", supplier: "J&J MedTech", stage: "Out for Delivery", carrier: "UPS Ground", eta: "Jun 17", color: COLORS.green, status: "On track" },
  { po: "PO#4833", product: "CORAIL Hip Stem Size 9", supplier: "DePuy Synthes", stage: "In Transit", carrier: "FedEx Freight", eta: "Jun 20", color: COLORS.amber, status: "At risk" },
  { po: "PO#4862", product: "ATTUNE Knee System", supplier: "DePuy Synthes", stage: "In Transit", carrier: "FedEx Priority", eta: "Jun 23", color: COLORS.amber, status: "At risk" },
  { po: "PO#4840", product: "HARMONIC Scalpel Handpiece", supplier: "Ethicon", stage: "Picked Up", carrier: "UPS Air", eta: "Jun 18", color: COLORS.green, status: "On track" },
  { po: "PO#4844", product: "THERMOCOOL SMARTTOUCH™ Catheter", supplier: "J&J MedTech", stage: "In Transit", carrier: "OnTrac", eta: "Jun 21", color: COLORS.green, status: "On track" },
];

const UPCOMING_DELIVERIES = [
  { po: "PO#4831", product: "ECHELON FLEX Stapler 60mm", sku: "JJ-ECH60", department: "OR Suite 3 — Cardiovascular", status: "On Track", statusColor: COLORS.green, etaDay: "Thu", etaDate: "Jun 19", imgBg: "#0f2a45", imgLabel: "EF" },
  { po: "PO#4838", product: "Endopath Trocar 12mm", sku: "ETH-EP12", department: "Central Sterile Supply", status: "On Track", statusColor: COLORS.green, etaDay: "Thu", etaDate: "Jun 19", imgBg: "#1a3a2a", imgLabel: "ET" },
  { po: "PO#4844", product: "THERMOCOOL SMARTTOUCH™ Catheter", sku: "BW-TCST-01", department: "Electrophysiology Lab / Cardiology", status: "On Track", statusColor: COLORS.green, etaDay: "Fri", etaDate: "Jun 20", imgBg: "#0d1f3a", imgLabel: "TC", imgSrc: "https://images.contentstack.io/v3/assets/blt6442fb89e58ceab5/blta430eb6c1c8784da/6973c7370398570c15826279/US_ELP_THER_406935_THERMOCOOL_SMARTTOUCH_SF_Page_Hero1.png?width=1920&quality=90&format=webp" },
  { po: "PO#4821", product: "VICRYL Suture 3-0 (Box/36)", sku: "ETH-VIC30", department: "OR Suite 1 — General Surgery", status: "Delayed", statusColor: COLORS.red, etaDay: "Sat", etaDate: "Jun 21", imgBg: "#3a1a1a", imgLabel: "VS" },
  { po: "PO#4849", product: "CORAIL Hip Stem Size 9", sku: "DPS-CRL-9", department: "Orthopedics — Novant Forsyth", status: "In Transit", statusColor: COLORS.blue, etaDay: "Sun", etaDate: "Jun 22", imgBg: "#2a1a3a", imgLabel: "CH" },
  { po: "PO#4852", product: "MONOCRYL Suture 4-0 (Box/12)", sku: "ETH-MON40", department: "OR Suite 2 — Laparoscopic", status: "In Transit", statusColor: COLORS.blue, etaDay: "Mon", etaDate: "Jun 23", imgBg: "#1a2a3a", imgLabel: "MS" },
  { po: "PO#4857", product: "HARMONIC Scalpel Handpiece", sku: "ETH-HSP-5", department: "OR Suite 4 — Robotics", status: "Processing", statusColor: COLORS.amber, etaDay: "Mon", etaDate: "Jun 23", imgBg: "#2a2a1a", imgLabel: "HS" },
  { po: "PO#4861", product: "ECHELON ENDOPATH™ Staple Line Reinforcement", sku: "ETH-ESLR-45", department: "OR Suite 1 — General Surgery", status: "On Track", statusColor: COLORS.green, etaDay: "Tue", etaDate: "Jun 24", imgBg: "#1a2a1a", imgLabel: "EE", imgSrc: "https://images.contentstack.io/v3/assets/blt6442fb89e58ceab5/blt2656e8d1af240a1a/689a479e6605f93933acc06f/US_SRG_STAP_123700.1_-_ECHELON_ENDOPATHTM_Staple_Line_Reinforcement_(Hero_1).png?width=1920&quality=90&format=webp" },
];

function DeliveryProductCard({ item, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      style={{
        ...cardStyle,
        flexShrink: 0,
        width: 180,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Product image with ETA overlay */}
      <div style={{
        width: "100%",
        height: 120,
        background: item.imgBg,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
      }}>
        {item.imgSrc
          ? <img src={item.imgSrc} alt={item.product} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
          : <span style={{ fontSize: 36, fontWeight: 800, color: "rgba(255,255,255,0.15)", letterSpacing: "0.06em" }}>{item.imgLabel}</span>
        }
        {/* Combined badge */}
        <div style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.55)", borderRadius: 20, padding: "2px 8px", border: `1px solid ${item.statusColor}`, display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 9, fontWeight: 600, color: "rgba(255,255,255,0.6)" }}>{item.etaDate}</span>
          <span style={{ fontSize: 8, color: "rgba(255,255,255,0.25)" }}>·</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: item.statusColor }}>{item.status}</span>
        </div>
      </div>
      {/* Info */}
      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>{item.po} · {item.sku}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.text, lineHeight: 1.3 }}>{item.product}</span>
        <span style={{ fontSize: 10, color: COLORS.textSecondary, lineHeight: 1.3 }}>{item.department}</span>
      </div>
    </motion.div>
  );
}

const ACTIONS_TAKEN = {
  "PO#4821": "Carrier notified — priority re-route filed",
  "PO#4809": "Monitoring — no action needed",
  "PO#4833": "Expedite request filed with DePuy Synthes",
  "PO#4862": "Transferred from South Park campus · Expedite filed",
  "PO#4840": "Monitoring — on track",
  "PO#4844": "Monitoring — on track",
};

const AUTONOMOUS_SHIPMENTS = ACTIVE_SHIPMENTS.map(r =>
  r.po === "PO#4862" || r.po === "PO#4821"
    ? { ...r, color: COLORS.green, status: "Resolved" }
    : r.po === "PO#4833"
    ? { ...r, color: COLORS.green, status: "Resolved" }
    : r
);

const AUTONOMOUS_KPIS = [
  { title: "In Transit", value: "28", caption: "Across 6 carriers" },
  { title: "Resolved", value: "3", caption: "Agent handled", captionColor: COLORS.green },
  { title: "On Time Today", value: "12", caption: "100% of scheduled", captionColor: COLORS.green },
  { title: "Expected Today", value: "5", caption: "Next: 2:40 PM EST" },
];

function StatusTrackingDashboard({ onShipmentSelect, autonomyLevel }) {
  const [selectedShipment, setSelectedShipment] = useState(null);

  const handleRowClick = (row) => {
    const next = selectedShipment?.po === row.po ? null : row;
    setSelectedShipment(next);
    onShipmentSelect?.(next);
  };

  const isAutonomous = autonomyLevel === 2;
  const isSemi = autonomyLevel === 1;
  const rows = (isAutonomous || isSemi) ? AUTONOMOUS_SHIPMENTS : ACTIVE_SHIPMENTS;
  const kpis = (isAutonomous || isSemi) ? AUTONOMOUS_KPIS : SHIPMENT_KPIS;
  const columns = (isAutonomous || isSemi)
    ? ["PO#", "Product", "Supplier", "Stage", "Carrier", "ETA", "Actions Taken"]
    : ["PO#", "Product", "Supplier", "Stage", "Carrier", "ETA", "Status"];

  return (
    <>
      <StatCards index={0} cards={kpis} />
      <Section index={1}>
        <SectionHeader
          title="Active Shipments"
          badge={(isAutonomous || isSemi)
            ? <Badge color={COLORS.green} bg="rgba(74,222,128,0.12)">3 Resolved</Badge>
            : <Badge color={COLORS.red} bg="rgba(235,23,0,0.12)">3 Delayed</Badge>
          }
        />
        <DataTable
          columns={columns}
          rows={rows}
          selectedRow={selectedShipment}
          onRowClick={handleRowClick}
          renderCell={(row, col) => {
            if (col === "PO#") return <span style={{ color: COLORS.text, fontWeight: 700 }}>{row.po}</span>;
            if (col === "Product") return <span style={{ color: COLORS.text, fontWeight: 600 }}>{row.product}</span>;
            if (col === "Supplier") return row.supplier;
            if (col === "Stage") return row.stage;
            if (col === "Carrier") return row.carrier;
            if (col === "ETA") return <span style={{ color: COLORS.text }}>{row.eta}</span>;
            if (col === "Actions Taken") {
              const action = ACTIONS_TAKEN[row.po];
              const hadAction = action && !action.toLowerCase().startsWith("monitoring");
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: isAutonomous ? COLORS.green : COLORS.text, fontWeight: 500 }}>
                    {action ?? "—"}
                  </span>
                  {isSemi && hadAction && (
                    <motion.button
                      whileHover={{ scale: 1.05, background: "rgba(167,139,250,0.2)" }}
                      whileTap={{ scale: 0.95 }}
                      onClick={e => { e.stopPropagation(); handleRowClick(row); }}
                      style={{ background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 700, color: "#A78BFA", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}
                    >
                      Review
                    </motion.button>
                  )}
                </div>
              );
            }
            return <StatusDot color={row.color} label={row.status} />;
          }}
        />
      </Section>
      <Section index={2}>
        <SectionHeader title="Upcoming Deliveries — Next 7 Days" />
        <div style={{
          display: "flex",
          gap: 10,
          overflowX: "auto",
          paddingBottom: 6,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}>
          {UPCOMING_DELIVERIES.map((d, i) => (
            <DeliveryProductCard key={d.po} item={d} index={i} />
          ))}
        </div>
      </Section>
    </>
  );
}

// ============================================================
// Creation & Replenishment Agent
// ============================================================
const INVENTORY_KPIS = [
  { title: "Below PAR", value: "6", caption: "3 facilities affected", captionColor: COLORS.amber },
  { title: "Critical (<7 days)", value: "2", caption: "Immediate reorder", captionColor: COLORS.red },
  { title: "Reorder Triggered", value: "4", caption: "Auto-generated POs" },
  { title: "Pending Approval", value: "2", caption: "Awaiting buyer sign-off" },
];

const REPLENISHMENT_QUEUE = [
  { item: "Endopath Trocar 12mm", sku: "ETH-EP12", stock: 14, days: 4, qty: 120, color: COLORS.red, status: "Critical" },
  { item: "ECHELON FLEX Stapler", sku: "JJ-ECH60", stock: 8, days: 6, qty: 40, color: COLORS.red, status: "Critical" },
  { item: "VICRYL Suture 3-0", sku: "ETH-VIC30", stock: 62, days: 11, qty: 300, color: COLORS.amber, status: "Below PAR" },
  { item: "MONOCRYL Suture 4-0", sku: "ETH-MON40", stock: 88, days: 13, qty: 250, color: COLORS.amber, status: "Below PAR" },
  { item: "Hip Stem - CORAIL", sku: "DPS-CRL-9", stock: 5, days: 18, qty: 12, color: COLORS.green, status: "Reorder set" },
  { item: "Knee Insert - ATTUNE", sku: "DPS-ATN-7", stock: 9, days: 21, qty: 18, color: COLORS.green, status: "Reorder set" },
];

const FACILITY_PAR = [
  { name: "Novant Presbyterian", color: COLORS.red, pos: 7 },
  { name: "Novant Forsyth", color: COLORS.amber, pos: 4 },
  { name: "Novant Matthews", color: COLORS.green, pos: 2 },
  { name: "Novant Huntersville", color: COLORS.green, pos: 3 },
  { name: "Novant Rowan", color: COLORS.amber, pos: 5 },
];

function CreationReplenishmentDashboard() {
  return (
    <>
      <StatCards index={0} cards={INVENTORY_KPIS} />
      <Section index={1}>
        <SectionHeader title="Replenishment Queue" badge={<Badge color={COLORS.red} bg="rgba(235,23,0,0.12)">2 Critical</Badge>} />
        <DataTable
          columns={["Item", "SKU", "Current Stock", "Days to Stockout", "Rec. Qty", "Status"]}
          rows={REPLENISHMENT_QUEUE}
          renderCell={(row, col) => {
            if (col === "Item") return <span style={{ color: COLORS.text, fontWeight: 600 }}>{row.item}</span>;
            if (col === "SKU") return row.sku;
            if (col === "Current Stock") return <span style={{ color: COLORS.text }}>{row.stock}</span>;
            if (col === "Days to Stockout") return <span style={{ color: row.color, fontWeight: 600 }}>{row.days}d</span>;
            if (col === "Rec. Qty") return <span style={{ color: COLORS.text }}>{row.qty}</span>;
            return <StatusDot color={row.color} label={row.status} />;
          }}
        />
      </Section>
      <Section index={2}>
        <SectionHeader title="Facility PAR Health" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
          {FACILITY_PAR.map((f) => (
            <div key={f.name} style={{ ...cardStyle, padding: 14, display: "flex", flexDirection: "column", gap: 8, borderTop: `2px solid ${f.color}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: f.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{f.name}</span>
              </div>
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>{f.pos} open POs</span>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

// ============================================================
// Validation Agent
// ============================================================
const VALIDATION_KPIS = [
  { title: "Validated Today", value: "14", caption: "Across 9 suppliers" },
  { title: "Failed Validation", value: "3", caption: "Pricing & contract", captionColor: COLORS.red },
  { title: "Pending", value: "5", caption: "In review queue", captionColor: COLORS.amber },
  { title: "Auto-Approved", value: "11", caption: "78.6% automation", captionColor: COLORS.green },
];

const PRICE_DISCREPANCIES = [
  { item: "Endopath Trocar 12mm", contract: "$184.00", actual: "$196.88", over: "+7.0%" },
  { item: "ECHELON FLEX Reload", contract: "$412.50", actual: "$433.13", over: "+5.0%" },
  { item: "VICRYL Suture 3-0 (bx)", contract: "$96.00", actual: "$99.84", over: "+4.0%" },
];

const VALIDATION_QUEUE = [
  { po: "PO#4815", supplier: "J&J MedTech", value: "$12,800", issue: "Invoice mismatch", color: COLORS.red },
  { po: "PO#4827", supplier: "DePuy Synthes", value: "$48,200", issue: "Price over contract", color: COLORS.amber },
  { po: "PO#4830", supplier: "Ethicon", value: "$6,450", issue: "Missing PO reference", color: COLORS.amber },
  { po: "PO#4835", supplier: "J&J MedTech", value: "$21,900", issue: "Quantity variance", color: COLORS.amber },
  { po: "PO#4842", supplier: "Ethicon", value: "$3,210", issue: "Awaiting contract refresh", color: COLORS.green },
];

function ValidationDashboard() {
  return (
    <>
      <StatCards index={0} cards={VALIDATION_KPIS} />
      <Section index={1}>
        <SectionHeader title="Price Discrepancies" badge={<Badge color={COLORS.red} bg="rgba(235,23,0,0.12)">3 Over Contract</Badge>} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {PRICE_DISCREPANCIES.map((d) => (
            <div key={d.item} style={{ ...cardStyle, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{d.item}</span>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                <span style={{ color: COLORS.textMuted }}>Contract</span>
                <span style={{ color: COLORS.textSecondary }}>{d.contract}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                <span style={{ color: COLORS.textMuted }}>Actual</span>
                <span style={{ color: COLORS.text, fontWeight: 600 }}>{d.actual}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.red }}>{d.over}</span>
                <button
                  type="button"
                  style={{ fontSize: 11, fontWeight: 600, color: COLORS.text, background: "rgba(255,255,255,0.06)", border: `1px solid ${COLORS.borderStrong}`, borderRadius: 6, padding: "5px 10px", cursor: "pointer" }}
                >
                  Flag to AP
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>
      <Section index={2}>
        <SectionHeader title="Validation Queue" badge={<Badge color={COLORS.amber} bg="rgba(251,191,36,0.12)">5 Pending</Badge>} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {VALIDATION_QUEUE.map((v) => (
            <div key={v.po} style={{ ...cardStyle, padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{v.po}</span>
                <span style={{ fontSize: 11, color: COLORS.textSecondary }}>{v.supplier}</span>
                <span style={{ fontSize: 11, color: COLORS.text, fontWeight: 600 }}>{v.value}</span>
              </div>
              <StatusDot color={v.color} label={v.issue} />
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

// ============================================================
// Exception Resolution Agent
// ============================================================
const EXCEPTION_KPIS = [
  { title: "Open Exceptions", value: "8", caption: "3 high severity", captionColor: COLORS.red },
  { title: "Resolved Today", value: "2", caption: "Avg 3.1h to close", captionColor: COLORS.green },
  { title: "Escalated", value: "1", caption: "To procurement lead", captionColor: COLORS.amber },
  { title: "SLA At Risk", value: "3", caption: "Within 4h of breach", captionColor: COLORS.red },
];

const OPEN_EXCEPTIONS = [
  { title: "Carrier reroute — PO#4821", color: COLORS.red, age: "6h open", status: "Expediting" },
  { title: "Invoice mismatch — INV-2887", color: COLORS.red, age: "1d open", status: "Awaiting AP" },
  { title: "Unacknowledged PO#4825", color: COLORS.red, age: "2d open", status: "EDI fallback" },
  { title: "Backorder — 4 SKUs no ETA", color: COLORS.amber, age: "3d open", status: "Sourcing alt" },
  { title: "Duplicate PO risk #4798", color: COLORS.amber, age: "5h open", status: "Pending review" },
];

const EXCEPTION_BREAKDOWN = [
  { type: "Shipment delays", count: 3 },
  { type: "Invoice/pricing", count: 3 },
  { type: "Acknowledgement", count: 1 },
  { type: "Backorder", count: 1 },
];

function ExceptionResolutionDashboard() {
  return (
    <>
      <StatCards index={0} cards={EXCEPTION_KPIS} />
      <Section index={1}>
        <SectionHeader title="Open Exceptions" badge={<Badge color={COLORS.red} bg="rgba(235,23,0,0.12)">3 High</Badge>} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {OPEN_EXCEPTIONS.map((e) => (
            <div key={e.title} style={{ ...cardStyle, padding: 14, display: "flex", flexDirection: "column", gap: 6, borderTop: `2px solid ${e.color}` }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{e.title}</span>
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>{e.age}</span>
              <StatusDot color={e.color} label={e.status} />
            </div>
          ))}
        </div>
      </Section>
      <Section index={2}>
        <SectionHeader title="Resolution Summary" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ ...cardStyle, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: COLORS.textSecondary, fontWeight: 600 }}>Resolved This Week</span>
              <span style={{ fontSize: 28, fontWeight: 700, color: COLORS.green }}>11</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: COLORS.textSecondary, fontWeight: 600 }}>Avg Resolution Time</span>
              <span style={{ fontSize: 28, fontWeight: 700, color: COLORS.text }}>4.2h</span>
            </div>
          </div>
          <div style={{ ...cardStyle, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: COLORS.textSecondary, fontWeight: 600 }}>By Type</span>
            {EXCEPTION_BREAKDOWN.map((b) => (
              <div key={b.type} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: COLORS.textSecondary }}>{b.type}</span>
                <span style={{ color: COLORS.text, fontWeight: 700 }}>{b.count}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </>
  );
}

// ============================================================
// PO-to-Invoice Matching Agent
// ============================================================
const MATCHING_KPIS = [
  { title: "Matched Today", value: "8", caption: "3-way match" },
  { title: "Auto-Approved", value: "6", caption: "75% straight-through", captionColor: COLORS.green },
  { title: "Exceptions", value: "1", caption: "Variance over threshold", captionColor: COLORS.red },
  { title: "Pending Match", value: "4", caption: "Awaiting receipt doc", captionColor: COLORS.amber },
];

const MATCHING_QUEUE = [
  { inv: "INV-2901", po: "PO#4809", supplier: "J&J MedTech", invAmt: "$28,500", poAmt: "$28,500", variance: "$0", color: COLORS.green, status: "Matched" },
  { inv: "INV-2887", po: "PO#4815", supplier: "J&J MedTech", invAmt: "$14,040", poAmt: "$12,800", variance: "+$1,240", color: COLORS.red, status: "Exception" },
  { inv: "INV-2895", po: "PO#4799", supplier: "Ethicon", invAmt: "$8,900", poAmt: "$8,900", variance: "$0", color: COLORS.green, status: "Matched" },
  { inv: "INV-2910", po: "PO#4831", supplier: "DePuy Synthes", invAmt: "$12,460", poAmt: "$12,400", variance: "+$60", color: COLORS.amber, status: "Review" },
  { inv: "INV-2914", po: "PO#4844", supplier: "J&J MedTech", invAmt: "—", poAmt: "$9,750", variance: "—", color: COLORS.amber, status: "Pending receipt" },
];

const MATCH_RATE_TREND = [
  { day: "Jun 11", rate: 92 },
  { day: "Jun 12", rate: 88 },
  { day: "Jun 13", rate: 95 },
  { day: "Jun 14", rate: 91 },
  { day: "Jun 15", rate: 97 },
  { day: "Jun 16", rate: 94 },
  { day: "Jun 17", rate: 96 },
];

function POInvoiceMatchingDashboard() {
  return (
    <>
      <StatCards index={0} cards={MATCHING_KPIS} />
      <Section index={1}>
        <SectionHeader title="Matching Queue" badge={<Badge color={COLORS.red} bg="rgba(235,23,0,0.12)">1 Exception</Badge>} />
        <DataTable
          columns={["Invoice #", "PO#", "Supplier", "Invoice Amount", "PO Amount", "Variance", "Status"]}
          rows={MATCHING_QUEUE}
          renderCell={(row, col) => {
            if (col === "Invoice #") return <span style={{ color: COLORS.text, fontWeight: 700 }}>{row.inv}</span>;
            if (col === "PO#") return row.po;
            if (col === "Supplier") return row.supplier;
            if (col === "Invoice Amount") return <span style={{ color: COLORS.text }}>{row.invAmt}</span>;
            if (col === "PO Amount") return <span style={{ color: COLORS.text }}>{row.poAmt}</span>;
            if (col === "Variance") return <span style={{ color: row.color, fontWeight: 600 }}>{row.variance}</span>;
            return <StatusDot color={row.color} label={row.status} />;
          }}
        />
      </Section>
      <Section index={2}>
        <SectionHeader title="Match Rate Trend — Last 7 Days" />
        <div style={{ ...cardStyle, padding: 16, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10, height: 160 }}>
          {MATCH_RATE_TREND.map((d) => (
            <div key={d.day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, height: "100%", justifyContent: "flex-end" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.text }}>{d.rate}%</span>
              <div style={{ width: "60%", height: `${d.rate}%`, background: COLORS.blue, borderRadius: "4px 4px 0 0" }} />
              <span style={{ fontSize: 9, color: COLORS.textMuted }}>{d.day}</span>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

// ============================================================
// Dashboard router
// ============================================================
const ORCH_STATS = [
  { label: "Open POs", value: "247" },
  { label: "Pending Approval", value: "18" },
  { label: "Delayed", value: "12" },
  { label: "Fill Rate", value: "97.8%" },
];

const PIPELINE_STAGES = [
  { label: "Draft",       count: 34,  sub: "awaiting submission" },
  { label: "Approved",    count: 41,  sub: "ready to send" },
  { label: "Sent",        count: 67,  sub: "with supplier" },
  { label: "Acknowledged",count: 52,  sub: "supplier confirmed" },
  { label: "Shipped",     count: 28,  sub: "in transit" },
  { label: "Received",    count: 15,  sub: "goods confirmed" },
  { label: "Invoiced",    count: 8,   sub: "awaiting payment" },
  { label: "Closed",      count: 142, sub: "complete" },
];
const PIPELINE_TOTAL = PIPELINE_STAGES.reduce((s, x) => s + x.count, 0);

const ORCH_AGENTS = [
  { name: "Status & Tracking",        task: "Reconciling ETAs on 12 active POs",     status: "Processing" },
  { name: "Creation & Replenishment", task: "6 SKUs queued for replenishment",        status: "Active" },
  { name: "Validation",               task: "3 POs pending price validation",         status: "Active" },
  { name: "Exception Resolution",     task: "PO#4815 invoice mismatch in review",     status: "Active" },
  { name: "PO-to-Invoice Matching",   task: "8 invoices awaiting 3-way match",        status: "Active" },
];

function OrchestratorDashboard() {
  const divider = { borderBottom: "1px solid rgba(255,255,255,0.06)" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>

      {/* Key numbers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, background: "rgba(255,255,255,0.06)", borderRadius: 10, overflow: "hidden" }}>
        {ORCH_STATS.map((s) => (
          <div key={s.label} style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(12px)", padding: "18px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 11, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600 }}>{s.label}</span>
            <span style={{ fontSize: 32, fontWeight: 700, color: COLORS.text, lineHeight: 1 }}>{s.value}</span>
          </div>
        ))}
      </div>

      {/* PO Pipeline */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0, borderRadius: 10, overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ padding: "12px 16px", background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", ...divider, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={sectionTitleStyle}>PO Pipeline</span>
          <span style={{ fontSize: 10, color: COLORS.textMuted }}>{PIPELINE_TOTAL} total</span>
        </div>
        {/* Stacked bar */}
        <div style={{ display: "flex", height: 6, background: "rgba(255,255,255,0.04)" }}>
          {PIPELINE_STAGES.map((s) => (
            <div key={s.label} style={{ flex: s.count, background: "rgba(255,255,255,0.18)", borderRight: "1px solid rgba(0,0,0,0.4)" }} />
          ))}
        </div>
        {/* Stage rows */}
        {PIPELINE_STAGES.map((s, i) => (
          <div
            key={s.label}
            style={{
              padding: "11px 16px",
              background: "rgba(0,0,0,0.2)",
              backdropFilter: "blur(12px)",
              display: "flex",
              alignItems: "center",
              gap: 12,
              ...(i < PIPELINE_STAGES.length - 1 ? divider : {}),
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, width: 120, flexShrink: 0 }}>{s.label}</span>
            <span style={{ fontSize: 11, color: COLORS.textSecondary, flex: 1 }}>{s.sub}</span>
            {/* Mini bar */}
            <div style={{ width: 80, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.06)", overflow: "hidden", flexShrink: 0 }}>
              <div style={{ width: `${(s.count / PIPELINE_TOTAL) * 100}%`, height: "100%", background: "rgba(255,255,255,0.3)", borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, width: 28, textAlign: "right", flexShrink: 0 }}>{s.count}</span>
          </div>
        ))}
      </div>

      {/* Sub-agent status */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0, borderRadius: 10, overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ padding: "12px 16px", background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", ...divider, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={sectionTitleStyle}>Agent Status</span>
          <span style={{ fontSize: 10, color: COLORS.textMuted }}>{ORCH_AGENTS.filter(a => a.status === "Active").length} active</span>
        </div>
        {ORCH_AGENTS.map((agent, i) => (
          <div
            key={i}
            style={{
              padding: "12px 16px",
              background: "rgba(0,0,0,0.2)",
              backdropFilter: "blur(12px)",
              display: "flex",
              alignItems: "center",
              gap: 12,
              ...(i < ORCH_AGENTS.length - 1 ? divider : {}),
            }}
          >
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: agent.status === "Active" ? COLORS.green : COLORS.amber, flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, width: 180, flexShrink: 0 }}>{agent.name}</span>
            <span style={{ fontSize: 11, color: COLORS.textSecondary, flex: 1 }}>{agent.task}</span>
          </div>
        ))}
      </div>

    </div>
  );
}

function renderDashboard(agent, onShipmentSelect, autonomyLevel) {
  switch (agent) {
    case "Status & Tracking":
      return <StatusTrackingDashboard onShipmentSelect={onShipmentSelect} autonomyLevel={autonomyLevel} />;
    case "Creation & Replenishment":
      return <CreationReplenishmentDashboard />;
    case "Validation":
      return <ValidationDashboard />;
    case "Exception Resolution":
      return <ExceptionResolutionDashboard />;
    case "PO-to-Invoice Matching":
      return <POInvoiceMatchingDashboard />;
    default:
      return <OrchestratorDashboard />;
  }
}

// ============================================================
// Root
// ============================================================
function PODashboard({ selectedAgent, onShipmentSelect, autonomyLevel = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      <div style={{ flex: 1, overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 40 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedAgent ?? "orchestrator"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{ display: "flex", flexDirection: "column", gap: 40 }}
          >
            {renderDashboard(selectedAgent, onShipmentSelect, autonomyLevel)}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default PODashboard;
